// src/components/Socio.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";

import ProductsPanel from "./ProductsPanel";

// Safe COLORS
let COLORS_SAFE = {};
try {
  // eslint-disable-next-line global-require
  COLORS_SAFE = require("../constants/ui").COLORS || {};
} catch {
  COLORS_SAFE = {};
}

const GlobalSocioCSS = () => (
  <style>{`
    :root{
      --text: #0f172a;
      --muted: rgba(15,23,42,.72);
      --accent: #06b6d4;
      --accent2:#2563eb;
      --ring: rgba(6,182,212,.35);
      --border: rgba(255,255,255,.22);
      --shadow: 0 18px 50px rgba(2,6,23,.18);
    }

    html, body, #root{ height:100%; }
    body{
      margin:0;
      color:var(--text);
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    /* viewport safe heights */
    .vh{
      min-height: 100vh;
      min-height: 100svh;
      min-height: 100dvh;
    }

    /* ✅ clave: permitir scroll del contenido sin romper el fondo */
    .page-root{
      position: relative;
      overflow: hidden;
    }
    .content{
      position: relative;
      z-index:2;
      height:100%;
      display:grid;
      align-items:center;
      padding: clamp(12px, 3.2vw, 32px);
      overflow: auto;                 /* ✅ scroll cuando sea necesario */
      overscroll-behavior: contain;   /* ✅ mejor sensación móvil */
      -webkit-overflow-scrolling: touch;
    }

    /* background slider full height */
    .bg-slider{
      position:absolute;
      inset:0;
      z-index:0;
    }
    .bg-slider .slick-slider,
    .bg-slider .slick-list,
    .bg-slider .slick-track,
    .bg-slider .slick-slide > div{
      height: 100%;
    }

    .bg-slide{ position: relative; width:100%; height:100%; }
    .bg-img{
      width:100%;
      height:100%;
      object-fit: cover;
      transform: scale(1.04);
      filter: saturate(1.05) contrast(1.02);
    }
    .bg-overlay{
      position:absolute;
      inset:0;
      background:
        radial-gradient(900px 600px at 12% 10%, rgba(37,99,235,.25), transparent 55%),
        radial-gradient(900px 600px at 88% 12%, rgba(6,182,212,.28), transparent 50%),
        radial-gradient(900px 600px at 30% 90%, rgba(99,102,241,.18), transparent 55%),
        linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.62));
    }

    .wrap{
      width: min(1180px, 94vw);   /* ✅ un poco más ancho en móvil */
      margin: 0 auto;
    }

    /* ✅ grid más inteligente */
    .grid{
      display:grid;
      gap: clamp(12px, 2.4vw, 20px);
      grid-template-columns: 1.15fr .85fr;
      align-items: start;
    }
    @media (max-width: 1100px){
      .grid{ grid-template-columns: 1fr; }
    }

    .card{
      background: rgba(255,255,255,.14);
      border: 1px solid var(--border);
      border-radius: 22px;
      box-shadow: var(--shadow);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      padding: clamp(14px, 3vw, 26px);
      color: #fff;
      min-width: 0; /* ✅ evita overflow en grids */
    }

    .brand{
      display:flex;
      align-items:center;
      gap: 12px;
      margin-bottom: 10px;
      min-width: 0;
    }

    .logo{
      width: 44px;
      height: 44px;
      border-radius: 14px;
      background: rgba(255,255,255,.18);
      border: 1px solid rgba(255,255,255,.22);
      display:flex;
      align-items:center;
      justify-content:center;
      overflow:hidden;
      flex: 0 0 auto;
    }
    .logo img{ width:100%; height:100%; object-fit: cover; }

    .title{
      margin: 0;
      font-size: clamp(22px, 4.2vw, 40px);
      font-weight: 900;
      letter-spacing: .2px;
      line-height: 1.08;
    }
    .title span{
      background: linear-gradient(90deg, var(--accent2), var(--accent));
      -webkit-background-clip:text;
      background-clip:text;
      color: transparent;
    }

    .lead{
      margin: 10px 0 0;
      font-size: clamp(14px, 1.9vw, 17px);
      line-height: 1.75;
      color: rgba(255,255,255,.88);
      max-width: 72ch;
    }

    /* ✅ botones responsive */
    .cta-row{
      display:grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
      margin-top: 16px;
    }
    @media (max-width: 740px){
      .cta-row{ grid-template-columns: 1fr; }
    }

    .btn{
      cursor:pointer;
      border: 0;
      border-radius: 14px;
      padding: 12px 14px;
      font-weight: 900;
      transition: transform .12s ease, filter .2s ease, opacity .2s ease;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      gap: 10px;
      user-select: none;
      white-space: nowrap;
      text-decoration:none;
      width: 100%;
      box-sizing: border-box;
    }
    .btn:active{ transform: translateY(1px); }
    .btn:hover{ filter: brightness(1.05); }
    .btn:disabled{ opacity:.6; cursor:not-allowed; }

    .btn-primary{
      background: linear-gradient(180deg, rgba(6,182,212,1), rgba(37,99,235,1));
      color:#062028;
      box-shadow: 0 12px 30px rgba(6,182,212,.18);
    }
    .btn-ghost{
      background: rgba(255,255,255,.14);
      color:#fff;
      border: 1px solid rgba(255,255,255,.22);
    }
    .btn-danger{
      background: rgba(255,255,255,.10);
      color:#fff;
      border: 1px solid rgba(255,255,255,.22);
    }

    .side{
      display:grid;
      gap: 12px;
      min-width: 0;
    }

    /* ✅ en pantallas medianas, las sidecards se vuelven grid */
    @media (max-width: 1100px){
      .side{
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
    @media (max-width: 740px){
      .side{
        grid-template-columns: 1fr;
      }
    }

    .sideCard{
      background: rgba(255,255,255,.12);
      border: 1px solid rgba(255,255,255,.20);
      border-radius: 18px;
      padding: 14px 14px;
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      color:#fff;
      min-width: 0;
    }

    .sideTitle{
      font-weight: 900;
      margin:0;
      font-size: 15px;
    }
    .sideText{
      margin: 6px 0 0;
      font-size: 13px;
      color: rgba(255,255,255,.84);
      line-height: 1.6;
    }

    .kv{
      display:grid;
      grid-template-columns: 1fr auto;
      gap: 8px 10px;
      margin-top: 10px;
      font-size: 13px;
      color: rgba(255,255,255,.86);
    }
    .kv b{ color: rgba(255,255,255,.96); font-weight: 900; }

    /* ✅ en móvil: kv apilado */
    @media (max-width: 480px){
      .kv{ grid-template-columns: 1fr; }
    }

    .pill{
      display:inline-flex;
      align-items:center;
      gap:8px;
      padding: 8px 10px;
      border-radius: 999px;
      background: rgba(255,255,255,.14);
      border: 1px solid rgba(255,255,255,.22);
      font-weight: 900;
      color:#fff;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .mini-foot{
      margin-top: 12px;
      font-size: 12px;
      color: rgba(255,255,255,.70);
    }

    .section-gap{ height: 18px; }

    /* ✅ ProductsPanel responsive: no overflow horizontal */
    .below{
      margin-top: 18px;
      display: grid;
      gap: 12px;
      min-width: 0;
    }
    .belowTitle{
      font-weight: 950;
      font-size: 14px;
      color: rgba(255,255,255,.92);
      margin: 0 0 10px;
    }
    .panel-embed{
      min-width: 0;
      overflow: hidden;
    }
    .panel-embed *{
      min-width: 0;
    }

    /* slick dots */
    .bg-slider .slick-dots{
      bottom: 16px;
      z-index: 6;
    }
    .bg-slider .slick-dots li button:before{
      color: rgba(255,255,255,.62);
      opacity: 1;
      font-size: 10px;
    }
    .bg-slider .slick-dots li.slick-active button:before{
      color: rgba(6,182,212,1);
    }

    .bg-slider .slick-prev, .bg-slider .slick-next{
      z-index: 7;
      width: 44px;
      height: 44px;
      border-radius: 999px;
      background: rgba(255,255,255,.16);
      border: 1px solid rgba(255,255,255,.22);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }
    .bg-slider .slick-prev{ left: 16px; }
    .bg-slider .slick-next{ right: 16px; }
    .bg-slider .slick-prev:before, .bg-slider .slick-next:before{
      color: rgba(255,255,255,.92);
      opacity: 1;
      font-size: 20px;
    }

    @media (max-width: 560px){
      .bg-slider .slick-prev,
      .bg-slider .slick-next{ display:none !important; }
      .bg-slider .slick-dots{ bottom: 10px; }
    }

    /* ✅ pantallas de poca altura: evita “cortar” */
    @media (max-height: 680px){
      .content{
        align-items: start;
        padding-top: 14px;
        padding-bottom: 38px;
      }
      .wrap{ padding-bottom: 18px; }
    }
  `}</style>
);

const fmtDate = (msOrTs) => {
  const ms =
    typeof msOrTs === "number"
      ? msOrTs
      : msOrTs?.toMillis?.()
      ? msOrTs.toMillis()
      : null;

  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
};

export default function Socio() {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [userDocId, setUserDocId] = useState("");
  const [loadingUser, setLoadingUser] = useState(false);

  const socio = useMemo(() => {
    const u = (localStorage.getItem("username") || "").trim();
    const uid = (localStorage.getItem("uid") || "").trim();
    return !!u || !!uid;
  }, []);

  const slides = useMemo(
    () => [
      {
        src: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?fm=jpg&q=60&w=2400&ixlib=rb-4.0.3",
        alt: "Ciudad y tráfico",
      },
      {
        src: "https://as2.ftcdn.net/v2/jpg/02/70/19/17/1000_F_270191793_5Nk3dvcKjuUgCuNJZ4tzL5tTNsjQlkqh.jpg",
        alt: "Rutas urbanas",
      },
      {
        src: "https://kudeko.com/cdn/shop/products/cuadro-nueva-york-ciudad-sin-marco-fotografia-blanco-negro_800x.jpg?v=1666951353",
        alt: "Ciudad nocturna",
      },
    ],
    []
  );

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 700,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4200,
      arrows: true,
      pauseOnHover: false,
      pauseOnFocus: false,
      swipe: true,
      touchMove: true,
      adaptiveHeight: false,
    }),
    []
  );

  const accent = COLORS_SAFE?.accent || "#06b6d4";

  const logout = () => {
    localStorage.removeItem("username");
    localStorage.removeItem("uid");
    navigate("/");
  };

  // ✅ Cargar userSocio (uid docId, o buscar por username)
  useEffect(() => {
    const run = async () => {
      if (!socio) return;

      setLoadingUser(true);
      try {
        const uid = (localStorage.getItem("uid") || "").trim();
        const username = (localStorage.getItem("username") || "")
          .trim()
          .toLowerCase();

        // 1) uid => doc directo
        if (uid) {
          const ref = doc(db, "userSocio", uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            setUserDocId(uid);
            setUserData({ id: uid, ...snap.data() });
            return;
          }
        }

        // 2) buscar por username
        if (username) {
          const q = query(
            collection(db, "userSocio"),
            where("username", "==", username),
            limit(1)
          );
          const res = await getDocs(q);
          if (!res.empty) {
            const d = res.docs[0];
            setUserDocId(d.id);
            setUserData({ id: d.id, ...d.data() });
            return;
          }
        }

        setUserDocId("");
        setUserData(null);
      } catch (e) {
        console.error("Error cargando userSocio:", e);
        setUserDocId("");
        setUserData(null);
      } finally {
        setLoadingUser(false);
      }
    };

    run();
  }, [socio]);

  const puntos = useMemo(() => {
    const n = Number(userData?.Cpuntos);
    return Number.isFinite(n) ? n : 0;
  }, [userData]);

  const role = userData?.role || userData?.Rol || "—";
  const codigoApp = userData?.codigoApp || "—";

  return (
    <>
      <GlobalSocioCSS />

      <div className="page-root vh">
        {/* Fondo */}
        <div className="bg-slider" aria-hidden="true">
          <Slider {...settings}>
            {slides.map((s, i) => (
              <div key={i}>
                <div className="bg-slide">
                  <img className="bg-img" src={s.src} alt={s.alt || "Slide"} />
                  <div className="bg-overlay" />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Contenido */}
        <div className="content">
          <div className="wrap">
            <div className="grid">
              <div className="card">
                <div className="brand">
                  <div className="logo">
                    <img src="/screen-do-logo.png" alt="Screen DO" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, color: "rgba(255,255,255,.92)" }}>
                      Screen DO
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                      Panel del Socio • Pagos • Actividad
                    </div>
                  </div>
                </div>

                {socio ? (
                  <>
                    <h1 className="title">
                      Tu <span>Panel</span> de Socio
                    </h1>

                    <p className="lead">
                      Aquí ves tu información y tus herramientas.{" "}
                      <strong style={{ color: accent }}>“Mi Pago”</strong> mostrará el historial mensual
                      cuando esté habilitado.
                    </p>

                    <div className="cta-row">
                      <button className="btn btn-primary" onClick={() => navigate("/mi-pago")}>
                        💳 Mi Pago
                      </button>

                      <button className="btn btn-ghost" onClick={() => navigate("/")}>
                        🏠 Ir al Inicio
                      </button>

                      <button className="btn btn-danger" onClick={logout}>
                        🚪 Cerrar sesión
                      </button>
                    </div>

                    <div className="mini-foot">
                      Tip: tu acento actual es{" "}
                      <strong style={{ color: accent }}>{accent}</strong>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="title">
                      Acceso <span>restringido</span>
                    </h1>

                    <p className="lead">
                      Debes iniciar sesión para ver el panel del socio. Si no tienes cuenta, créala en segundos.
                    </p>

                    <div className="cta-row">
                      <Link className="btn btn-primary" to="/login">
                        🔑 Ir a Login
                      </Link>
                      <Link className="btn btn-ghost" to="/signup">
                        📝 Crear cuenta
                      </Link>
                      <Link className="btn btn-ghost" to="/">
                        🏠 Volver
                      </Link>
                    </div>
                  </>
                )}
              </div>

              <div className="side">
                <div className="sideCard">
                  <p className="sideTitle">👤 Datos del socio</p>

                  {loadingUser ? (
                    <p className="sideText">Cargando datos…</p>
                  ) : !userData ? (
                    <p className="sideText">
                      No se encontraron datos en <b>userSocio</b>. Verifica que exista el documento del usuario.
                    </p>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                        <span className="pill">⭐ Cpuntos: {puntos}</span>
                        <span className="pill">🏷️ Rol: {role}</span>
                      </div>

                      <div className="kv">
                        <div><b>DocID</b></div><div style={{ opacity: 0.95 }}>{userDocId || "—"}</div>
                        <div><b>códigoApp</b></div><div style={{ opacity: 0.95 }}>{codigoApp}</div>
                        <div><b>Creado</b></div><div style={{ opacity: 0.95 }}>{fmtDate(userData?.createdAt)}</div>
                        <div><b>Actualizado</b></div><div style={{ opacity: 0.95 }}>{fmtDate(userData?.updatedAt)}</div>
                      </div>
                    </>
                  )}
                </div>

                <div className="sideCard">
                  <p className="sideTitle">📅 Pagos mensuales</p>
                  <p className="sideText">
                    “Mi Pago” mostrará el mes en curso y el historial desde la fecha de creación de tu cuenta.
                  </p>
                </div>

                <div className="sideCard">
                  <p className="sideTitle">⏳ Estado del pago</p>
                  <p className="sideText">
                    Etapas: <strong>Procesando</strong> (Propago=true) y <strong>Pago</strong> (Epago=true).
                  </p>
                </div>

                <div className="sideCard">
                  <p className="sideTitle">🧾 Desglose</p>
                  <p className="sideText">
                    Comisión publicidad (Pcomicionpublic), pago conductor (Pagconductor), comisión producto
                    (pagproduc) y total.
                  </p>
                </div>
              </div>
            </div>

            <div className="section-gap" />

            {/* ✅ Bloque inferior con ProductsPanel (responsive safe) */}
            {socio && (
              <div className="below">
                <div className="card" style={{ padding: 14 }}>
                  <p className="belowTitle">🛍️ Productos / Ventas / Logros</p>
                  <div className="panel-embed">
                    <ProductsPanel
                      userData={userData}
                      setUserData={setUserData}
                      userDocId={userDocId}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="section-gap" />
          </div>
        </div>
      </div>
    </>
  );
}
